import { useInView, useMotionValue, useSpring } from "motion/react";
import { useCallback, useEffect, useRef } from "react";

type CountUpProps = {
	to: number;
	from?: number;
	direction?: "up" | "down";
	delay?: number;
	duration?: number;
	className?: string;
	startWhen?: boolean;
	separator?: string;
	onStart?: () => void;
	onEnd?: () => void;
	"aria-hidden"?: boolean | "true" | "false";
	"aria-live"?: "off" | "polite" | "assertive";
	"aria-atomic"?: boolean | "true" | "false";
};

export function CountUp({
	to,
	from = 0,
	direction = "up",
	delay = 0,
	duration = 2,
	className = "",
	startWhen = true,
	separator = "",
	onStart,
	onEnd,
	"aria-hidden": ariaHidden,
	"aria-live": ariaLive = "polite",
	"aria-atomic": ariaAtomic = "true",
}: CountUpProps) {
	const ref = useRef<HTMLSpanElement>(null);
	const motionValue = useMotionValue(direction === "down" ? to : from);

	const damping = 20 + 40 * (1 / duration);
	const stiffness = 100 * (1 / duration);

	const springValue = useSpring(motionValue, {
		damping,
		stiffness,
	});

	const isInView = useInView(ref, { once: true, margin: "0px" });

	const getDecimalPlaces = (num: number): number => {
		const str = num.toString();

		if (str.includes(".")) {
			const decimals = str.split(".")[1];
			if (Number.parseInt(decimals, 10) !== 0) return decimals.length;
		}

		return 0;
	};

	const maxDecimals = Math.max(getDecimalPlaces(from), getDecimalPlaces(to));

	const formatValue = useCallback(
		(latest: number) => {
			const hasDecimals = maxDecimals > 0;

			const options: Intl.NumberFormatOptions = {
				useGrouping: !!separator,
				minimumFractionDigits: hasDecimals ? maxDecimals : 0,
				maximumFractionDigits: hasDecimals ? maxDecimals : 0,
			};

			const formattedNumber = Intl.NumberFormat("en-US", options).format(latest);

			return separator ? formattedNumber.replace(/,/g, separator) : formattedNumber;
		},
		[maxDecimals, separator],
	);

	useEffect(() => {
		if (ref.current) {
			ref.current.textContent = formatValue(direction === "down" ? to : from);
		}
	}, [from, to, direction, formatValue]);

	useEffect(() => {
		if (isInView && startWhen) {
			if (typeof onStart === "function") {
				onStart();
			}

			const timeoutId = setTimeout(() => {
				motionValue.set(direction === "down" ? from : to);
			}, delay * 1000);

			const durationTimeoutId = setTimeout(
				() => {
					if (typeof onEnd === "function") {
						onEnd();
					}
				},
				delay * 1000 + duration * 1000,
			);

			return () => {
				clearTimeout(timeoutId);
				clearTimeout(durationTimeoutId);
			};
		}
	}, [isInView, startWhen, motionValue, direction, from, to, delay, onStart, onEnd, duration]);

	useEffect(() => {
		const unsubscribe = springValue.on("change", (latest: number) => {
			if (ref.current) {
				ref.current.textContent = formatValue(latest);
			}
		});

		return () => unsubscribe();
	}, [springValue, formatValue]);

	return (
		<span
			ref={ref}
			className={className}
			aria-hidden={ariaHidden}
			aria-live={ariaHidden ? undefined : ariaLive}
			aria-atomic={ariaHidden ? undefined : ariaAtomic}
		/>
	);
}
